/**
 * axiosBootstrap.js — App-wide axios behaviour.
 *
 * Phase 57b — Dedupes the GET /api/auth/me thundering-herd.
 *
 * Multiple components (Dashboard, Banners, hooks) fire `axios.get('/auth/me')`
 * concurrently on every page render. Previously this caused 4-9 round-trips.
 *
 * Strategy (one shared Promise per page):
 *   1. The very first concurrent caller gets a real network fetch (via a
 *      fresh `axios.get(..., __authMePassthrough: true)` whose adapter is
 *      NOT touched by this interceptor).
 *   2. Every subsequent concurrent caller's adapter is replaced with a
 *      function that resolves from the shared `inFlight` Promise.
 *   3. `inFlight` is captured SYNCHRONOUSLY inside the request interceptor
 *      (not inside the adapter lambda) so multiple components mounting in
 *      the same tick all see the same promise.
 *   4. After resolution, the response is memoised for AUTH_ME_CACHE_TTL_MS
 *      so subsequent navigations reuse it.
 *   5. The cache is busted on any login/logout/register mutation.
 *
 * Scope: ONLY GET /auth/me. Everything else passes through untouched.
 */
import axios from 'axios';

const AUTH_ME_CACHE_TTL_MS = 30 * 1000;
let inFlight = null;       // Promise<AxiosResponse> | null
let cachedResponse = null; // { data, status, headers } | null
let cachedAt = 0;

const isAuthMeGet = (config) => {
  if (!config) return false;
  const url = (config.baseURL || '') + (config.url || '');
  const method = (config.method || 'get').toLowerCase();
  return method === 'get' && /\/auth\/me(?:[/?]|$)/.test(url);
};

const isAuthMutation = (config) => {
  if (!config) return false;
  const url = (config.baseURL || '') + (config.url || '');
  const method = (config.method || 'get').toLowerCase();
  return (method === 'post' || method === 'delete')
    && /\/(auth|logout)\/(login|register|logout|password|reset|google)/i.test(url);
};

const fakeFromCached = (config) => ({
  data: cachedResponse.data,
  status: cachedResponse.status,
  statusText: 'OK',
  headers: cachedResponse.headers || {},
  config,
  request: null,
});

const fakeFromResponse = (response, config) => ({
  data: response.data,
  status: response.status,
  statusText: response.statusText || 'OK',
  headers: response.headers || {},
  config,
  request: null,
});

axios.interceptors.request.use((config) => {
  // Auth mutation — bust the cache.
  if (isAuthMutation(config)) {
    inFlight = null;
    cachedResponse = null;
    cachedAt = 0;
    return config;
  }

  // Passthrough flag means "this is OUR internal fetch — do not dedupe."
  if (!isAuthMeGet(config) || config.__authMePassthrough) {
    return config;
  }

  const now = Date.now();
  const fresh = cachedResponse && (now - cachedAt) < AUTH_ME_CACHE_TTL_MS;

  if (fresh) {
    config.adapter = () => Promise.resolve(fakeFromCached(config));
    return config;
  }

  // SYNCHRONOUS in-flight capture: if no concurrent call has started yet,
  // start one RIGHT NOW (still inside the interceptor) so subsequent callers
  // in the same tick see `inFlight` set.
  if (!inFlight) {
    inFlight = axios
      .get(config.url, { withCredentials: true, __authMePassthrough: true })
      .then((response) => {
        cachedResponse = {
          data: response.data,
          status: response.status,
          headers: response.headers,
        };
        cachedAt = Date.now();
        return response;
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          cachedResponse = null;
          cachedAt = Date.now();
        }
        throw err;
      })
      .finally(() => { inFlight = null; });
  }

  // Every caller (including the one whose call triggered the inFlight
  // creation) hangs off the shared promise — no extra network request.
  const sharedPromise = inFlight;
  config.adapter = () => sharedPromise.then(
    (response) => fakeFromResponse(response, config),
    (err) => Promise.reject(err),
  );
  return config;
});
